import { CornerDownLeft, Search } from 'lucide-react';

import { paraBuscar } from './search-text';
import { useId, useMemo, useState, type KeyboardEvent } from 'react';

import { cn } from './cn';
import { Kbd } from './kbd';

export interface CommandItem {
  readonly id: string;
  readonly label: string;
  readonly group: string;
  readonly hint?: string;
  readonly disabled?: boolean;
  readonly run: () => void;
}

interface CommandPaletteProps {
  readonly onClose: () => void;
  readonly items: readonly CommandItem[];
}

/**
 * Paleta de comandos.
 *
 * En una herramienta operativa el teclado no es un extra: es la diferencia
 * entre navegar cuatro menus y escribir tres letras. La spec 73 pide
 * explicitamente que las operaciones frecuentes no cuesten diez clics.
 *
 * IMPORTANTE: el componente asume que esta abierto. Es el padre quien decide
 * montarlo o no. Asi el estado interno -consulta y seleccion- se reinicia al
 * montar, sin necesidad de sincronizarlo con un efecto, que es justo el
 * antipatron que React desaconseja.
 *
 * El filtro es por subcadena y no difuso a proposito: con un catalogo de
 * acciones acotado, el difuso produce coincidencias sorprendentes, y la
 * sorpresa es lo ultimo que se quiere en una herramienta que se usa de
 * memoria todos los dias.
 */
export function CommandPalette({ onClose, items }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const listId = useId();

  // Los modulos aun no disponibles SI se listan, atenuados y no
  // seleccionables. Ocultarlos haria que buscar "kardex" no devolviera nada
  // pese a que la seccion aparece en el menu, y eso se lee como un fallo de
  // la busqueda, no como una funcion pendiente.
  const matches = useMemo(() => {
    const needle = paraBuscar(query);
    if (needle === '') return items;
    return items.filter(
      (item) => paraBuscar(item.label).includes(needle) || paraBuscar(item.group).includes(needle),
    );
  }, [items, query]);

  // El indice se acota AL LEER, no con un efecto que lo corrija despues de
  // pintar: si el filtro reduce la lista, el cursor nunca apunta fuera de
  // rango en ningun fotograma.
  const activeIndex = nearestSelectable(matches, cursor);

  const move = (delta: number): void => {
    const next = stepToSelectable(matches, activeIndex, delta);
    if (next !== null) setCursor(next);
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    const actions: Record<string, () => void> = {
      Escape: onClose,
      ArrowDown: () => {
        move(1);
      },
      ArrowUp: () => {
        move(-1);
      },
      Enter: () => {
        const selected = matches[activeIndex];
        if (selected === undefined || selected.disabled === true) return;
        selected.run();
        onClose();
      },
    };

    const action = actions[event.key];
    if (action === undefined) return;
    event.preventDefault();
    action();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]"
      role="presentation"
      onMouseDown={onClose}
    >
      <div className="absolute inset-0 bg-n950/35 backdrop-blur-[2px]" aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paleta de comandos"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative w-full max-w-lg overflow-hidden rounded-lg border border-line-strong',
          'bg-panel shadow-(--shadow-modal)',
          'motion-safe:animate-[pop_var(--t-ui)_var(--ease-out)]',
        )}
      >
        <div className="flex items-center gap-2.5 border-b border-line px-3.5">
          <Search className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
          <input
            // Escribir es lo unico que se hace aqui: obligar a pinchar primero
            // anularia el sentido del atajo.
            autoFocus
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setCursor(0);
            }}
            placeholder="Buscar seccion o accion"
            aria-controls={listId}
            aria-autocomplete="list"
            className="h-11 w-full bg-transparent text-base text-fg outline-none placeholder:text-fg-subtle"
          />
          <Kbd>Esc</Kbd>
        </div>

        <ul id={listId} role="listbox" className="max-h-72 overflow-y-auto p-1.5">
          {matches.length === 0 ? (
            <li className="px-3 py-7 text-center text-sm text-fg-muted">
              Ninguna coincidencia para <span className="font-medium">{query}</span>
            </li>
          ) : (
            matches.map((item, index) => (
              <li key={item.id}>
                <CommandRow
                  item={item}
                  selected={index === activeIndex}
                  onHover={() => {
                    setCursor(index);
                  }}
                  onPick={() => {
                    if (item.disabled === true) return;
                    item.run();
                    onClose();
                  }}
                />
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

function CommandRow({
  item,
  selected,
  onHover,
  onPick,
}: {
  item: CommandItem;
  selected: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const unavailable = item.disabled === true;

  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      aria-disabled={unavailable || undefined}
      onMouseEnter={onHover}
      onClick={onPick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left',
        'transition-colors duration-(--t-tap)',
        unavailable && 'cursor-default text-fg-subtle',
        selected && !unavailable ? 'bg-brand-tint text-fg' : !unavailable && 'text-fg-body',
      )}
    >
      <span className="col-head w-24 shrink-0 truncate">{item.group}</span>
      <span className="flex-1 truncate text-sm">{item.label}</span>
      {item.hint !== undefined && (
        <span className="shrink-0 text-2xs text-fg-subtle">{item.hint}</span>
      )}
      <CornerDownLeft
        className={cn(
          'size-3.5 shrink-0 text-brand-ink transition-opacity duration-(--t-tap)',
          selected && !unavailable ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden="true"
      />
    </button>
  );
}

/** Primer indice seleccionable a partir de `from`, o `from` si no hay ninguno. */
function nearestSelectable(items: readonly CommandItem[], from: number): number {
  if (items.length === 0) return 0;
  const start = Math.min(from, items.length - 1);

  for (let offset = 0; offset < items.length; offset += 1) {
    const index = (start + offset) % items.length;
    if (items[index]?.disabled !== true) return index;
  }
  return start;
}

/** Avanza `delta` posiciones saltando lo no seleccionable. */
function stepToSelectable(
  items: readonly CommandItem[],
  from: number,
  delta: number,
): number | null {
  if (items.length === 0) return null;

  for (let step = 1; step <= items.length; step += 1) {
    const index = (items.length + from + delta * step) % items.length;
    if (items[index]?.disabled !== true) return index;
  }
  return null;
}
